import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { toast } from '../lib/toast';
import { errorFields } from '../lib/api-error';
import { AREA_LABELS, ROLE_LABELS, type PermissionArea } from '../lib/permissions';
import { dateTime } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Input,
  PageHeader,
  Select,
  Table,
  Td,
  Th,
} from '../components/ui';

interface TwoFactorSetupResponse {
  otpauthUrl: string;
  secret: string;
}

interface TwoFactorEnableResponse {
  backupCodes: string[];
}

export function ProfilePage() {
  const { user, signOut } = useAuth();
  if (!user) return null;

  return (
    <>
      <PageHeader title="Profil" description={user.email} />

      <div className="space-y-4">
        <Card>
          <CardHeader title="Hesap" />
          <div className="space-y-2 px-4 py-3 text-sm">
            <div className="flex items-baseline justify-between">
              <span className="text-[var(--c-ink-soft)]">Ad</span>
              <span className="font-medium">{user.name}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-[var(--c-ink-soft)]">Rol</span>
              <Badge tone="brand">{ROLE_LABELS[user.role]}</Badge>
            </div>
            <div>
              <span className="text-[var(--c-ink-soft)]">Erişebildiğiniz bölümler</span>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {user.areas.map((area) => (
                  <Badge key={area}>{AREA_LABELS[area as PermissionArea] ?? area}</Badge>
                ))}
              </div>
              {user.permissions === null && (
                <p className="mt-1.5 text-xs text-[var(--c-ink-faint)]">
                  Rolünüzün varsayılan yetkileri geçerli — size özel bir izin listesi tanımlanmamış.
                </p>
              )}
            </div>
          </div>
        </Card>

        <PasswordCard onReauthRequired={() => void signOut()} />
        <TwoFactorCard enabled={user.twoFactorEnabled} onReauthRequired={() => void signOut()} />
        <ApiTokensCard twoFactorEnabled={user.twoFactorEnabled} />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────── şifre değiştir

function PasswordCard({ onReauthRequired }: { onReauthRequired: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [invalid, setInvalid] = useState<Set<string>>(new Set());

  const change = useMutation({
    mutationFn: () =>
      api.post('/auth/change-password', { currentPassword: current, newPassword: next }),
    onSuccess: () => {
      /*
       * Sunucu şifre değişiminde tokenVersion'ı artırır ve TÜM oturumları
       * düşürür — elimizdeki token o an geçersizdir. Kullanıcıyı burada
       * bilinçli olarak çıkışa alıyoruz; aksi hâlde sonraki her istek
       * TOKEN_STALE ile döner ve sebebi anlaşılmaz görünür.
       */
      toast.success('Şifreniz değişti. Yeni şifrenizle tekrar giriş yapın.');
      onReauthRequired();
    },
    onError: (err) => {
      setInvalid(errorFields(err));
      toast.error(err);
    },
  });

  const mismatch = repeat.length > 0 && next !== repeat;

  return (
    <Card>
      <CardHeader
        title="Şifre değiştir"
        description="Şifre değiştiğinde tüm cihazlardaki oturumlar kapanır."
      />
      <div className="space-y-3 px-4 py-3">
        <Input
          label="Mevcut şifre"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <Input
          label="Yeni şifre"
          type="password"
          autoComplete="new-password"
          hint="En az 12 karakter."
          error={invalid.has('newPassword') ? 'Şifre en az 12 karakter olmalı.' : null}
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <Input
          label="Yeni şifre (tekrar)"
          type="password"
          autoComplete="new-password"
          error={mismatch ? 'Şifreler eşleşmiyor.' : null}
          value={repeat}
          onChange={(e) => setRepeat(e.target.value)}
        />
        <Button
          loading={change.isPending}
          disabled={!current || next.length < 12 || mismatch}
          onClick={() => change.mutate()}
        >
          Şifreyi değiştir
        </Button>
      </div>
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────── 2FA

function TwoFactorCard({
  enabled,
  onReauthRequired,
}: {
  enabled: boolean;
  onReauthRequired: () => void;
}) {
  const [setup, setSetup] = useState<TwoFactorSetupResponse | null>(null);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  /*
   * Kurulum ve etkinleştirme de ŞİFRE ister. Sebep: çalınmış bir access token
   * ile saldırgan kendi doğrulayıcısını bağlayıp meşru kullanıcıyı hesabından
   * kalıcı olarak kilitleyebilirdi — etkinleştirme tüm oturumları düşürür.
   */
  const start = useMutation({
    mutationFn: () => api.post<TwoFactorSetupResponse>('/auth/2fa/setup', { password }),
    onSuccess: (res) => setSetup(res),
    onError: (err) => toast.error(err),
  });

  const enable = useMutation({
    mutationFn: () =>
      api.post<TwoFactorEnableResponse>('/auth/2fa/enable', { password, code: code.trim() }),
    onSuccess: (res) => {
      setBackupCodes(res.backupCodes);
      setSetup(null);
      setCode('');
      setPassword('');
      toast.success('İki adımlı doğrulama açıldı.');
    },
    onError: (err) => toast.error(err),
  });

  const disable = useMutation({
    mutationFn: () => api.post('/auth/2fa/disable', { password, code: code.trim() }),
    onSuccess: () => {
      // Sunucu tokenVersion'ı artırdı; bu oturum zaten geçersiz.
      toast.success('İki adımlı doğrulama kapatıldı. Tekrar giriş yapın.');
      onReauthRequired();
    },
    onError: (err) => toast.error(err),
  });

  if (backupCodes) {
    return (
      <Card className="border-[var(--c-warn)]">
        <CardHeader
          title="Yedek kodlarınız"
          description="Bu kodlar BİR KEZ gösterilir. Şimdi kaydedin — doğrulama uygulamanıza erişemezseniz tek giriş yolunuz bunlar."
        />
        <div className="px-4 py-3">
          <ul className="grid grid-cols-2 gap-1 font-mono text-sm">
            {backupCodes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <Button
            variant="secondary"
            className="mt-3"
            onClick={() => {
              setBackupCodes(null);
              // Oturum tokenVersion nedeniyle düşmüş olabilir; sayfayı tazele.
              window.location.reload();
            }}
          >
            Kaydettim
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="İki adımlı doğrulama"
        description="Doğrulama uygulaması (TOTP) ile giriş. Açıldığında diğer oturumlar kapanır."
      />
      <div className="space-y-3 px-4 py-3">
        {enabled ? (
          <>
            <p>
              <Badge tone="ok">açık</Badge>
            </p>
            <p className="text-sm text-[var(--c-ink-soft)]">
              Kapatmak için şifrenizi ve geçerli bir doğrulama kodunu (veya kullanılmamış bir yedek
              kodu) girin.
            </p>
            <Input
              label="Şifre"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Input
              label="Doğrulama kodu"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <Button
              variant="danger"
              loading={disable.isPending}
              disabled={!password || !code}
              onClick={() => disable.mutate()}
            >
              İki adımlı doğrulamayı kapat
            </Button>
          </>
        ) : setup ? (
          <>
            <p className="text-sm text-[var(--c-ink-soft)]">
              Doğrulama uygulamanıza aşağıdaki anahtarı girin, sonra ürettiği 6 haneli kodu yazın.
            </p>
            <code className="block break-all rounded-[var(--r-md)] bg-[var(--c-surface-alt)] px-3 py-2 font-mono text-sm">
              {setup.secret}
            </code>
            <Input
              label="Doğrulama kodu"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <div className="flex gap-2">
              <Button loading={enable.isPending} disabled={!code} onClick={() => enable.mutate()}>
                Etkinleştir
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setSetup(null);
                  setPassword('');
                  setCode('');
                }}
              >
                Vazgeç
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-[var(--c-ink-soft)]">
              Kurulumu başlatmak için şifrenizi girin. Şifre istenmesi bilinçli: çalınmış bir oturum
              tek başına ikinci faktörü bağlayıp sizi hesabınızdan kilitleyemesin.
            </p>
            <Input
              label="Şifre"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              variant="secondary"
              loading={start.isPending}
              disabled={!password}
              onClick={() => start.mutate()}
            >
              İki adımlı doğrulamayı kur
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────── komut satırı anahtarları

interface ApiTokenView {
  id: string;
  name: string;
  tokenPrefix: string;
  readOnly: boolean;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

const API_TOKENS_KEY = ['auth', 'api-tokens'] as const;

/**
 * `cm` komut satırı aracının anahtarları.
 *
 * ⚠ Anahtar YALNIZ sunucu içi kapıda geçer (CLI_PORT, 127.0.0.1); bu panelin
 * bağlandığı adreste reddedilir. Kart bunu söylüyor ki anahtarı "her yerden
 * kullanılabilir bir parola" sanan olmasın.
 */
function ApiTokensCard({ twoFactorEnabled }: { twoFactorEnabled: boolean }) {
  const qc = useQueryClient();
  const tokens = useQuery({
    queryKey: API_TOKENS_KEY,
    queryFn: () => api.get<ApiTokenView[]>('/auth/api-tokens'),
  });

  const [name, setName] = useState('');
  const [days, setDays] = useState('90');
  const [readOnly, setReadOnly] = useState(false);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [created, setCreated] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post<ApiTokenView & { token: string }>('/auth/api-tokens', {
        name: name.trim(),
        expiresInDays: Number(days),
        readOnly,
        password,
        ...(code.trim() ? { code: code.trim() } : {}),
      }),
    onSuccess: (res) => {
      setCreated(res.token);
      setName('');
      setPassword('');
      setCode('');
      void qc.invalidateQueries({ queryKey: API_TOKENS_KEY });
    },
    onError: (err) => toast.error(err),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.post<ApiTokenView>(`/auth/api-tokens/${id}/revoke`),
    onSuccess: () => {
      toast.success('Anahtar iptal edildi.');
      void qc.invalidateQueries({ queryKey: API_TOKENS_KEY });
    },
    onError: (err) => toast.error(err),
  });

  const live = (t: ApiTokenView) =>
    !t.revokedAt && (!t.expiresAt || new Date(t.expiresAt).getTime() > Date.now());

  return (
    <Card>
      <CardHeader
        title="Komut satırı anahtarları"
        description="Sunucudaki cm aracının anahtarları. Yalnız sunucunun içinden geçerlidir; bu panelin adresinden ve internetten kullanılamaz."
      />
      <div className="space-y-3 px-4 py-3">
        {created && (
          <div className="space-y-2 rounded-[var(--r-md)] border border-[var(--c-warn)] p-3">
            <p className="text-sm font-medium">
              Anahtar BİR KEZ gösterilir. Sunucuda cm kullanıcısının ~/.centramir/token dosyasına
              yazın; başka hiçbir yere kaydetmeyin.
            </p>
            <code className="block break-all rounded-[var(--r-md)] bg-[var(--c-surface-alt)] px-3 py-2 font-mono text-sm">
              {created}
            </code>
            <Button variant="secondary" onClick={() => setCreated(null)}>
              Kaydettim
            </Button>
          </div>
        )}

        {tokens.data && tokens.data.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th>Ad</Th>
                <Th>Önek</Th>
                <Th>Son kullanım</Th>
                <Th>Bitiş</Th>
                <Th>Durum</Th>
                <Th>{''}</Th>
              </tr>
            </thead>
            <tbody>
              {tokens.data.map((t) => (
                <tr key={t.id}>
                  <Td>
                    {t.name}
                    {t.readOnly && (
                      <span className="ml-1">
                        <Badge>salt okuma</Badge>
                      </span>
                    )}
                  </Td>
                  <Td>
                    <span className="font-mono text-xs">{t.tokenPrefix}…</span>
                  </Td>
                  <Td>{t.lastUsedAt ? dateTime(t.lastUsedAt) : '—'}</Td>
                  <Td>{t.expiresAt ? dateTime(t.expiresAt) : '—'}</Td>
                  <Td>
                    {t.revokedAt ? (
                      <Badge>iptal</Badge>
                    ) : live(t) ? (
                      <Badge tone="ok">geçerli</Badge>
                    ) : (
                      <Badge>süresi doldu</Badge>
                    )}
                  </Td>
                  <Td>
                    {!t.revokedAt && (
                      <Button
                        variant="secondary"
                        loading={revoke.isPending && revoke.variables === t.id}
                        onClick={() => revoke.mutate(t.id)}
                      >
                        İptal et
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <Input
          label="Anahtarın adı"
          placeholder="Claude — muhasebe"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Select label="Geçerlilik" value={days} onChange={(e) => setDays(e.target.value)}>
          <option value="30">30 gün</option>
          <option value="90">90 gün</option>
          <option value="180">180 gün</option>
          <option value="366">1 yıl</option>
        </Select>
        <Checkbox
          label="Salt okuma"
          hint="İşaretliyse anahtar hiçbir kaydı ekleyemez, değiştiremez, silemez."
          checked={readOnly}
          onChange={(e) => setReadOnly(e.target.checked)}
        />
        <Input
          label="Şifre"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {twoFactorEnabled && (
          <Input
            label="Doğrulama kodu"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        )}
        <Button
          loading={create.isPending}
          disabled={!name.trim() || !password || (twoFactorEnabled && !code.trim())}
          onClick={() => create.mutate()}
        >
          Anahtar oluştur
        </Button>
      </div>
    </Card>
  );
}
